import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, Search } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : null);
const kb = (b) => (b ? `${Math.round(b / 1024)} KB` : "–");

// Admin: keep link previews warm (WhatsApp gives up after a few seconds on a cold page) and check any URL as WhatsApp would.
export default function LinkPreviewCard({ headers }) {
  const [last, setLast] = useState(null);
  const [warming, setWarming] = useState(false);
  const [url, setUrl] = useState("https://www.savicoliving.com/sojourns");
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = () => axios.get(`${API}/admin/seo/warm`, { headers }).then((r) => setLast(r.data.last || null)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const warm = async () => {
    setWarming(true);
    try {
      const r = await axios.post(`${API}/admin/seo/warm`, {}, { headers });
      toast.success(`Warming ${r.data.count} pages, give it a minute`);
      setTimeout(() => { load(); setWarming(false); }, 45000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start");
      setWarming(false);
    }
  };

  const run = async () => {
    if (!url.trim()) return;
    setChecking(true);
    setCheck(null);
    try {
      const r = await axios.get(`${API}/admin/seo/preview-check`, { params: { url: url.trim() }, headers });
      setCheck(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  const slow = last?.results?.filter((r) => r.warm > 2 || !r.status) || [];

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-6" data-testid="admin-link-previews">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-medium text-[#2E2A26]">Link previews</h2>
          <p className="text-xs text-[#3D4035]/60 mt-1 max-w-xl">
            WhatsApp only shows a card if the page answers within a couple of seconds. The first time a bot asks for a page the host renders it (6 to 11 s), after that it's instant. Every morning at 06:00 and after each "Tell search engines" the Sojourns pages are fetched as a bot so they stay warm. Share the exact link, without extra ?tags.
          </p>
        </div>
        <Button onClick={warm} disabled={warming} variant="outline" className="rounded-full" data-testid="admin-warm-btn">
          <Flame className="w-4 h-4 mr-2" /> {warming ? "Warming" : "Warm previews now"}
        </Button>
      </div>
      <p className="text-xs mt-3" data-testid="admin-warm-last">
        {last?.value
          ? <span className={slow.length ? "text-amber-700" : "text-emerald-700"}>Last warmed {fmt(last.value)} · {last.results.length} pages{slow.length ? ` · ${slow.length} still slow` : ", all answering in under 2 s"}.</span>
          : <span className="text-[#3D4035]/50">Not warmed yet.</span>}
      </p>

      <div className="mt-5 pt-5 border-t border-stone-100">
        <label htmlFor="preview-url" className="block text-xs text-[#3D4035]/60 mb-1">Check a link the way WhatsApp sees it</label>
        <div className="flex flex-wrap gap-2">
          <input id="preview-url" value={url} onChange={(e) => setUrl(e.target.value)} className="h-10 flex-1 min-w-[260px] px-3 rounded-lg border border-stone-200 text-sm bg-white" data-testid="admin-preview-url" />
          <Button onClick={run} disabled={checking} className="bg-[#2E2A26] hover:bg-black text-white rounded-full h-10" data-testid="admin-preview-check-btn">
            <Search className="w-4 h-4 mr-2" /> {checking ? "Fetching" : "Check"}
          </Button>
        </div>
        {check && (
          <div className="mt-3 rounded-lg bg-[#FBF7F1] p-3 text-xs space-y-1.5" data-testid="admin-preview-result">
            <p className={check.verdict === "ok" ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"} data-testid="admin-preview-verdict">
              {check.verdict === "ok" && `Answered in ${check.seconds}s with a ${kb(check.image_bytes)} image. WhatsApp will show the card.`}
              {check.verdict === "slow" && `Answered in ${check.seconds}s, too slow for WhatsApp this time. It's warm now; check again.`}
              {check.verdict === "problem" && (check.error || `HTTP ${check.status}${check.og?.["og:image"] ? "" : ", no og:image found"}${check.image_bytes >= 600000 ? ", image too heavy" : ""}`)}
            </p>
            {check.og?.["og:image"] && <img src={check.og["og:image"]} alt="" className="w-64 rounded border border-stone-200" />}
            <p className="text-[#2E2A26]"><b>Title:</b> {check.og?.["og:title"] || check.title || "–"}</p>
            <p className="text-[#3D4035]/70"><b>Description:</b> {check.og?.["og:description"] || "–"}</p>
            <p className="text-[#3D4035]/50 break-all"><b>Final URL:</b> {check.final_url}</p>
          </div>
        )}
      </div>
    </div>
  );
}
